import React, { useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Save as SaveIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { marcaService, Marca, MarcaRequest } from '../../services/marca';

interface MarcaFormProps {
  isEdit?: boolean;
}

const MarcaForm: React.FC<MarcaFormProps> = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = React.useState({
    nome: '',
    ativo: true,
  });
  
  const [errors, setErrors] = React.useState({
    nome: '',
  });

  // Buscar marca para edição
  const { data: marca, isLoading } = useQuery({
    queryKey: ['marca', id],
    queryFn: () => marcaService.getById(Number(id)),
    enabled: isEdit && !!id,
  });

  // Mutation para salvar (criar ou editar)
  const saveMutation = useMutation({
    mutationFn: (data: MarcaRequest) => {
      if (isEdit && id) {
        return marcaService.update(data);
      } else {
        return marcaService.save(data);
      }
    },
    onSuccess: () => {
      // Invalidar o cache para forçar atualização
      queryClient.invalidateQueries({ queryKey: ['marcas'] });
      navigate('/marcas');
    },
    onError: (error: any) => {
      console.error('Erro ao salvar marca:', error);
    },
  });

  useEffect(() => {
    if (marca && isEdit) {
      setFormData({
        nome: marca.nome,
        ativo: marca.ativo === 'SIM',
      });
    }
  }, [marca, isEdit]);

  const validateForm = (): boolean => {
    const newErrors = {
      nome: '',
    };
    let isValid = true;

    // Validação Nome
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
      isValid = false;
    } else if (formData.nome.length < 3) {
      newErrors.nome = 'Nome não deve possuir menos que 3 caracteres';
      isValid = false;
    } else if (formData.nome.length > 30) {
      newErrors.nome = 'Nome não deve possuir mais que 30 caracteres';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      const marcaData: MarcaRequest = {
        id: isEdit ? Number(id) : undefined,
        nome: formData.nome.trim(),
        ativo: formData.ativo ? 'SIM' : 'NAO',
      };
      
      saveMutation.mutate(marcaData);
    }
  };

  const handleClear = () => {
    setFormData({
      nome: '',
      ativo: true,
    });
    setErrors({
      nome: '',
    });
  };

  const handleBack = () => {
    navigate('/marcas');
  };

  const handleInputChange = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = field === 'ativo' ? e.target.checked : e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Limpar erro do campo quando usuário começar a digitar
    if (errors.nome && field === 'nome') {
      setErrors(prev => ({
        ...prev,
        nome: '',
      }));
    }
  };

  if (isEdit && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            fullWidth
            label="Marca"
            value={formData.nome}
            onChange={handleInputChange('nome')}
            error={!!errors.nome}
            helperText={errors.nome}
            slotProps={{
              htmlInput: { maxLength: 30 }
            }}
            required
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={formData.ativo}
                onChange={handleInputChange('ativo')}
                color="primary"
              />
            }
            label={formData.ativo ? 'Ativo' : 'Inativo'}
          />
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saveMutation.isPending}
              sx={{ minWidth: 120 }}
            >
              {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
            
            <Button
              type="button"
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClear}
              sx={{ minWidth: 120 }}
            >
              Limpar
            </Button>
            
            <Button
              type="button"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ minWidth: 120 }}
            >
              Voltar
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default MarcaForm;
